import { useId } from "react";
import { useQuery } from "@tanstack/react-query";
import { creatorScoreAPIClient } from "@/marketing/infrastructure/influencer/campaign/score/creatorScoreAPIClient";
import { MARKETING_ROUTES } from "@/marketing/routes";
import type { CreatorScoreIdentity } from "@/marketing/models/creatorScore";
import {
  CREATOR_SCORE_TIER_LABELS,
  EXCELLENT_SCORE,
  formatCreatorScore,
  formatCreatorScoreMeasurement,
  formatCreatorScoreWeight,
  getCreatorScoreMetricLabel,
  getCreatorScoreMetricStatusLabel,
  getCreatorScoreMetricSummary,
  getCreatorScoreTier,
  isBelowMinimumScore,
  MINIMUM_QUALIFYING_SCORE,
  SCORE_REVIEW_LABELS,
  type CreatorScoreSnapshot,
} from "@/marketing/models/creatorScore";
import { useCreatorScores } from "./CreatorScoreProvider";
import { SQUARE_VIEW_FORMULA, SquareViewCalculationDetails } from "./SquareViewCalculationDetails";
function EvidenceLinks({ urls }: { urls: string[] }) {
  return (
    <>
      {urls
        .filter((url) => /^https?:\/\//i.test(url))
        .map((url, index) => (
          <a
            key={`${index}-${url}`}
            className="tw-mr-3 tw-text-primary tw-underline"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Source {index + 1}
          </a>
        ))}
    </>
  );
}
function CalculationDetails({ score }: { score: CreatorScoreSnapshot }) {
  if (score.schemaVersion === 4 && score.formulaVersion === SQUARE_VIEW_FORMULA) {
    return <SquareViewCalculationDetails score={score} />;
  }
  const calculation = score.calculation ?? {};
  const fields = score.schemaVersion === 4
    ? [
        { label: "Content score", value: calculation["contentScore"], isPoints: false },
        { label: "Internal score", value: calculation["internalScore"], isPoints: false },
      ]
    : [
        { label: "Retained v3", value: calculation["v3Score"], isPoints: false },
        { label: "v4 branch", value: calculation["v4Score"], isPoints: false },
        { label: "Content score", value: calculation["contentScore"], isPoints: false },
        { label: "Completion bonus", value: calculation["completionBonus"], isPoints: true },
        { label: "Capacity penalty", value: calculation["capacityPenalty"], isPoints: true },
      ];
  const recordedFields = fields.filter(({ value }) =>
    value === null || (typeof value === "number" && Number.isFinite(value)),
  );
  return (
    <details className="tw-rounded tw-border tw-p-3">
      <summary className="tw-cursor-pointer tw-font-semibold">Calculation details</summary>
      <div className="tw-mt-3 tw-space-y-1">
        {recordedFields.length === 0 ? (
          <p>No calculation breakdown is stored for this snapshot.</p>
        ) : recordedFields.map(({ label, value, isPoints }) => (
          <p key={label}>
            {label}: {isPoints && typeof value === "number"
              ? `${value.toFixed(2)} points`
              : formatCreatorScore(typeof value === "number" ? value : null)}
          </p>
        ))}
      </div>
      <p className="tw-mt-2 tw-text-xs tw-text-muted-foreground">
        These values come from the stored snapshot. The final score is not recalculated here.
      </p>
    </details>
  );
}
export function CreatorScoreDetail({
  campaignId,
  creator,
}: {
  campaignId: string;
  creator: CreatorScoreIdentity;
}) {
  const evidenceId = useId();
  const { scores } = useCreatorScores();
  const query = useQuery({
    queryKey: ["creatorScoreDetail", campaignId, creator.influencerId],
    queryFn: () => creatorScoreAPIClient.getCampaignCreatorScore(campaignId, creator.influencerId),
    enabled: creator.userId != null,
    retry: false,
    throwOnError: false,
  });
  if (creator.userId == null)
    return <p className="tw-py-4 tw-text-sm" title="Hub user not linked">—</p>;
  if (query.isLoading)
    return <p className="tw-py-4 tw-text-sm">Loading score breakdown…</p>;
  if (query.isError)
    return (
      <div role="alert" className="tw-py-4 tw-text-sm">
        Score details unavailable.{" "}
        <button className="tw-underline" onClick={() => void query.refetch()}>
          Retry
        </button>
      </div>
    );
  const score = query.data?.snapshot;
  if (!score)
    return (
      <p className="tw-py-4 tw-text-sm" title="No stored snapshot is available">—</p>
    );
  const summary = scores[creator.userId];
  const tier = getCreatorScoreTier(score.finalScore, score.reviewStatus);
  const historyMetrics = score.metrics.filter((metric) =>
    ["completion", "timeliness", "repeat", "internal_score"].includes(metric.metric),
  );
  const hasNoCampaignHistory = historyMetrics.length > 0 && historyMetrics.every((metric) => metric.score == null);
  return (
    <div className="tw-space-y-5 tw-py-5 tw-text-sm">
      <div className="tw-rounded-lg tw-border tw-p-4">
        {score.reviewStatus === "blocked" && (
          <div className="tw-mb-3 tw-rounded tw-border tw-border-red-200 tw-bg-red-50 tw-p-3 tw-text-red-800 dark:tw-border-red-800 dark:tw-bg-red-950 dark:tw-text-red-200">
            <p className="tw-font-semibold">This creator is blocked in the stored review.</p>
            <a className="tw-underline" href={`#${evidenceId}`}>View recorded reasons</a>
          </div>
        )}
        <div className={`tw-text-3xl tw-font-semibold tw-tabular-nums ${isBelowMinimumScore(score.finalScore) ? "tw-text-amber-700 dark:tw-text-amber-400" : ""}`}>
          {formatCreatorScore(score.finalScore)}
        </div>
        <p className="tw-mt-1 tw-font-medium" data-testid="creator-score-tier">
          {CREATOR_SCORE_TIER_LABELS[tier]} · minimum qualifying {MINIMUM_QUALIFYING_SCORE.toFixed(1)} · excellent{" "}
          {EXCELLENT_SCORE.toFixed(1)}+
        </p>
        {hasNoCampaignHistory && tier !== "blocked" && (
          <p className="tw-mt-1 tw-text-amber-700 dark:tw-text-amber-400">
            No SkinSeoul campaign history yet. The score cannot reflect reliability for first-time creators; review
            manually.
          </p>
        )}
        <p className="tw-mt-2 tw-text-xs tw-text-muted-foreground">
          {SCORE_REVIEW_LABELS[score.reviewStatus]} · {score.formulaVersion} ·{" "}
          {score.freshness}
        </p>
        <p className="tw-mt-2 tw-text-xs tw-text-muted-foreground">
          Calculated: {score.calculatedAt}
          <br />
          Snapshot cutoff: {score.inputCutoff}
          <br />
          Coverage: {score.coverage.toFixed(1)}% · Revision {score.revision}
        </p>
        {summary && summary.revision !== score.revision && (
          <p className="tw-mt-2">
            This detail has a different revision from the list. Reload the
            campaign to align them.
          </p>
        )}
      </div>
      {score.baseScore !== score.finalScore && (
        <p>
          Base {formatCreatorScore(score.baseScore)} → Final{" "}
          {formatCreatorScore(score.finalScore)}. See review evidence below.
        </p>
      )}
      <section>
        <h3 className="tw-mb-2 tw-font-semibold">Score inputs</h3>
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-text-left tw-text-xs">
            <thead>
              <tr>
                <th>What we measured</th>
                <th>Measurement</th>
                <th>Score / 10</th>
                <th>Weight</th>
              </tr>
            </thead>
            <tbody>
              {score.metrics.map((metric) => (
                <tr
                  key={metric.metric}
                  className="tw-border-t tw-align-top"
                >
                  <td className="tw-py-2 tw-pr-2">
                    <span className="tw-font-medium">{getCreatorScoreMetricLabel(metric.metric)}</span>
                    <span className="tw-ml-2 tw-text-muted-foreground">
                      {getCreatorScoreMetricStatusLabel(metric.status)}
                    </span>
                    <div className="tw-text-muted-foreground">
                      {getCreatorScoreMetricSummary(metric.metric) ?? metric.reason}
                    </div>
                    {getCreatorScoreMetricSummary(metric.metric) && (
                      <details className="tw-text-muted-foreground">
                        <summary className="tw-cursor-pointer">Technical note</summary>
                        <p className="tw-mt-1">{metric.reason}</p>
                      </details>
                    )}
                    <EvidenceLinks urls={metric.evidence} />
                  </td>
                  <td className="tw-tabular-nums">{formatCreatorScoreMeasurement(metric.rawValue, metric.unit)}</td>
                  <td className="tw-tabular-nums">
                    {metric.score == null ? "—" : metric.score.toFixed(1)}
                  </td>
                  <td className="tw-tabular-nums">{formatCreatorScoreWeight(metric.weight)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <CalculationDetails score={score} />
      <section id={evidenceId}>
        <h3 className="tw-font-semibold">
          Review evidence · revision {score.riskRevision}
        </h3>
        {score.riskCases.length === 0 ? (
          <p className="tw-text-muted-foreground">
            No cases recorded. This is not a guarantee of reliability.
          </p>
        ) : (
          score.riskCases.map((risk) => (
            <div key={risk.caseId} className="tw-mt-2 tw-rounded tw-border tw-p-3">
              <p className="tw-font-medium">
                {risk.status} ·{" "}
                {risk.kind === "ghosting"
                  ? "Ghosting"
                  : "Operational blacklist"}
              </p>
              <p>{risk.reason}</p>
              {risk.campaignId && (
                <a
                  className="tw-underline"
                  href={MARKETING_ROUTES.campaignDetail(risk.campaignId)}
                >
                  Campaign evidence
                </a>
              )}
              <div>
                <EvidenceLinks urls={risk.sourceReferences} />
              </div>
            </div>
          ))
        )}
      </section>
      <section>
        <h3 className="tw-font-semibold">Source observation dates</h3>
        {score.sources.map((source) => (
          <p key={source.name}>
            {source.name}: {source.observedAt}
          </p>
        ))}
      </section>
    </div>
  );
}
